import { createContext, useSyncExternalStore, type ReactElement } from "react";
import { Button, ListRow, logical } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { MAP_SCALE } from "../world-map/geometry.ts";
import { MARK_SIZES } from "../world-map/labels.ts";
import { panelBox } from "../world-map/layout.ts";
import type { Size } from "odyssey-shared";
import { travelGroups, travelTitle } from "./lines.ts";
import type { TravelSnapshot, TravelStore } from "./store.ts";

/**
 * The world map in travel mode (ADR-137 decision 22; the design's world map card's list style and its "Travel action on
 * shrine marks and list lines", a visual reference): the offer's places as marks on the map, a shrine's diamond or a
 * waypoint's stone, and the Known places panel replaced by the offer's lines grouped by region, each with its price in
 * coin or "Free" and its Focus, a line that cannot be paid now dimmed with its reason. A click on a line or its mark, or
 * the arrow keys, picks it; the Travel button, Enter on a line or a second click on the picked mark sends `travel`.
 * Closing the map discards the offer (the store does).
 */

/**
 * What the world map reads and acts on: the travel store's reading side and its picks.
 */
export type TravelControl = Pick<TravelStore, "subscribe" | "getSnapshot" | "pick" | "explain" | "travel" | "ask">;

/**
 * Reads the travel store's snapshot, or a snapshot with no offer when the page has no travel store.
 *
 * @param travel The travel store, if the page has one.
 * @returns The snapshot.
 */
export function useTravel(travel: TravelControl | undefined): TravelSnapshot | null {
  return useSyncExternalStore(travel?.subscribe ?? noSubscription, () => travel?.getSnapshot() ?? null, () => travel?.getSnapshot() ?? null);
}

/**
 * A subscription that never fires.
 *
 * @returns A function that stops listening, which does nothing.
 */
function noSubscription(): () => void {
  return () => undefined;
}

/**
 * The window's title while it shows an offer, or null for the map's own heading.
 *
 * @param snapshot The travel snapshot.
 * @returns The title.
 */
export function travelHeading(snapshot: TravelSnapshot | null): string | null {
  return snapshot?.offer == null ? null : travelTitle(snapshot.offer);
}

/**
 * The offer's places on this map as marks, in picture pixels inside the map's marks layer: a shrine's diamond or a
 * waypoint's stone, the picked one ringed, a dimmed one faded. A place on another map has no mark.
 *
 * @param props The `snapshot` with the offer and the `travel` store a press picks through.
 * @returns The marks.
 */
export function TravelMarks({ snapshot, travel }: { readonly snapshot: TravelSnapshot; readonly travel: TravelControl }): ReactElement | null {
  const offer = snapshot.offer;
  if (offer === null) {
    return null;
  }
  const size = MARK_SIZES.shrine;
  return (
    <>
      {offer.places.map((place, index) =>
        place.map !== "" ? null : (
          <button
            key={`travel-${index}`}
            type="button"
            className={`ot-worldmap__mark ot-worldmap__mark--${place.kind}${place.affordable ? "" : " is-dimmed"}${snapshot.picked === index ? " is-picked" : ""}`}
            data-worldmap="travel-mark"
            data-mark={place.kind}
            aria-label={place.name}
            tabIndex={-1}
            style={{ left: logical(Math.round(place.x * MAP_SCALE - size / 2)), top: logical(Math.round(place.y * MAP_SCALE - size / 2)), width: logical(size), height: logical(size) }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => (!place.affordable ? travel.explain(index) : snapshot.picked === index ? travel.travel(index) : travel.pick(index))}
          >
            {place.kind === "shrine" ? <span className="ot-worldmap__core" aria-hidden="true" /> : null}
          </button>
        ),
      )}
    </>
  );
}

/**
 * The travel panel over the map in place of Known places: its title's heading, the lines by region, and the Travel
 * button under them, enabled while a line that can be paid is picked.
 *
 * @param props The `snapshot`, the `travel` store, the `view` it is laid in, and the `mapName` a place without a region
 *   is grouped under.
 * @returns The panel.
 */
export function TravelPanel({ snapshot, travel, view, mapName }: { readonly snapshot: TravelSnapshot; readonly travel: TravelControl; readonly view: Size; readonly mapName: string }): ReactElement | null {
  const offer = snapshot.offer;
  if (offer === null) {
    return null;
  }
  const box = panelBox(view, false);
  const groups = travelGroups(offer, mapName);
  const order = groups.flatMap((group) => group.lines.filter((line) => line.dimmed === "").map((line) => line.index));
  const picked = offer.places[snapshot.picked];
  return (
    <div className="ot-worldmap__places ot-travel" data-worldmap="travel" role="group" aria-label={say("travel.places")} style={{ left: logical(box.left), top: logical(box.top), width: logical(box.width), maxHeight: logical(box.height) }} onPointerDown={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
      <div className="ot-worldmap__heading" aria-hidden="true">
        <span>{say("travel.places")}</span>
        <span className="ot-worldmap__count">{offer.places.length}</span>
      </div>
      <div
        role="listbox"
        aria-label={say("travel.places")}
        className="ot-travel__list ot-scroll"
        data-travel="list"
        onKeyDown={(event) => {
          if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
            return;
          }
          event.preventDefault();
          const at = order.indexOf(snapshot.picked);
          const next = order[Math.min(order.length - 1, Math.max(0, at + (event.key === "ArrowDown" ? 1 : -1)))];
          if (next !== undefined) {
            travel.pick(next);
          }
        }}
      >
        {groups.length === 0 ? (
          <div className="ot-worldmap__note" data-worldmap="note" role="status">
            {say("travel.none")}
          </div>
        ) : null}
        {groups.map((group) => (
          <div key={group.region} role="presentation" className="ot-travel__region">
            <div className="ot-travel__group" data-travel="region">
              <span>{group.region}</span>
            </div>
            {group.lines.map((line) => (
              <div key={line.index} role="presentation" className="ot-travel__line" data-travel="line" data-dimmed={line.dimmed === "" ? undefined : ""}>
                <ListRow
                  label={line.name}
                  lead={{ node: <span className={`ot-travel__kind ot-travel__kind--${line.place.kind}`} aria-hidden="true" />, height: 8 }}
                  sub={line.dimmed !== "" ? line.dimmed : line.focus === "" ? undefined : line.focus}
                  trailing={line.price}
                  selected={line.index === snapshot.picked}
                  disabled={line.dimmed !== ""}
                  tabStop={line.index === snapshot.picked || (snapshot.picked < 0 && line.index === order[0])}
                  onPick={() => travel.pick(line.index)}
                  onActivate={() => travel.travel(line.index)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="ot-travel__footer" data-travel="footer">
        {snapshot.refusal === "" ? null : (
          <span className="ot-travel__refusal" data-travel="refusal" role="status">
            {snapshot.refusal}
          </span>
        )}
        <Button variant="primary" disabled={picked === undefined || !picked.affordable} onClick={() => travel.travel()}>
          {say("travel.travel")}
        </Button>
      </div>
    </div>
  );
}

/**
 * The refusal of a request that opened no offer, over the map where the message line is covered by the window.
 *
 * @param props The `snapshot` whose refusal is shown.
 * @returns The note, or nothing while there is no refusal or an offer is shown.
 */
export function TravelNote({ snapshot }: { readonly snapshot: TravelSnapshot }): ReactElement | null {
  if (snapshot.offer !== null || snapshot.refusal === "") {
    return null;
  }
  return (
    <div className="ot-travel__note" data-travel="note" role="status">
      {snapshot.refusal}
    </div>
  );
}

/**
 * The travel store the world map's views read, provided by the window around its body so the views it lays out need
 * not pass it down; undefined on a page without travel (the pop-out window, the gallery).
 */
export const TravelContext = createContext<TravelControl | undefined>(undefined);
